import React from 'react';
import VirtualizedMessageList from '../VirtualizedMessageList';
import MessageBubble from '../MessageBubble';
import OrderMessage from './OrderMessage';
import MessageStatus from './MessageStatus';

/**
 * MessageList Component
 * 
 * Main message list container that handles rendering of all messages
 * Includes virtualization for large message lists and special handling for order messages
 * 
 * @param {Array} allVisibleMessages - Array of all visible messages
 * @param {Object} user - Current user object
 * @param {boolean} isAdminDashboard - Whether in admin dashboard
 * @param {Object} conversation - Current conversation object
 * @param {Function} onImageClick - Handler for image clicks
 * @param {Function} renderMessageStatus - Function to render message status
 * @param {Function} onReplyClick - Handler for reply clicks
 * @param {Function} onContextMenu - Handler for context menu
 * @param {boolean} isDarkMode - Dark mode state
 * @param {Object} fileHandling - File handling utilities
 * @param {Function} dispatch - State dispatch function
 * @returns {JSX.Element} Message list component
 */
const MessageList = ({
  allVisibleMessages,
  user,
  isAdminDashboard,
  conversation,
  onImageClick,
  renderMessageStatus,
  onReplyClick,
  onContextMenu,
  isDarkMode,
  fileHandling,
  dispatch
}) => {
  // ⚡️ PERFORMANCE OPTIMIZATION: Use virtualization for large message lists
  if (allVisibleMessages.length > 100) {
    return (
      <VirtualizedMessageList
        messages={allVisibleMessages}
        user={user}
        isAdminDashboard={isAdminDashboard}
        conversation={conversation}
        onImageClick={onImageClick}
        renderMessageStatus={renderMessageStatus}
        containerHeight={600}
        itemHeight={80}
      />
    );
  }

  // Regular message list rendering
  return (
    <>
      {allVisibleMessages.map((msg, idx) => {
        // Special handling for order messages
        if (msg.type === 'order') {
          return (
            <OrderMessage
              key={msg.id || idx}
              msg={msg}
              idx={idx}
              isDarkMode={isDarkMode}
            />
          );
        }

        // Regular message rendering using MessageBubble
        return (
          <MessageBubble
            key={msg.id || idx}
            msg={msg}
            user={user}
            isAdminDashboard={isAdminDashboard}
            conversation={conversation}
            onImageClick={onImageClick}
            renderMessageStatus={renderMessageStatus}
            onReplyClick={onReplyClick}
            replyToMessage={msg.replyTo}
            onContextMenu={onContextMenu}
          />
        );
      })}
    </>
  );
};

export default MessageList;


